import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, X, Minus, ShieldCheck } from "lucide-react";

const COLS = ["oresto", "excel", "fiduciary", "pos"];

function Cell({ cell }) {
  const [kind, note] = cell;
  const icon = {
    yes: <Check className="w-5 h-5 text-primary-c" strokeWidth={3} />,
    no: <X className="w-5 h-5 text-red-500" strokeWidth={2.5} />,
    partial: <Minus className="w-5 h-5 text-amber-600" strokeWidth={3} />,
  }[kind];
  return (
    <div className="flex flex-col items-center gap-0.5 text-center">
      {icon}
      {note && <span className={`${kind === "text" ? "text-xs font-semibold text-[var(--text)]" : "text-[11px] text-text-secondary"} leading-snug`}>{note}</span>}
    </div>
  );
}

export default function ComparisonTable() {
  const { t } = useTranslation();
  const rows = t("compare.rows", { returnObjects: true });
  const heads = { oresto: "Oresto", excel: "Excel", fiduciary: t("compare.col_fiduciary"), pos: t("compare.col_pos") };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6 }} className="text-center">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-secondary-c bg-secondary-c/10 px-3 py-1 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" /> {t("compare.badge")}
        </span>
        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-4 leading-tight max-w-3xl mx-auto">
          {t("compare.title")} <span className="font-serif-italic text-primary-c">{t("compare.title_accent")}</span>
        </h2>
      </motion.div>

      {/* Mobile: one card per criterion */}
      <div className="mt-8 space-y-3 md:hidden" data-testid="comparison-cards">
        {rows.map((r) => (
          <div key={r.label} className="card-c !p-4">
            <p className="font-semibold text-sm">{r.label}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {COLS.map((c) => (
                <div key={c} className={`rounded-xl p-2.5 ${c === "oresto" ? "bg-primary-c/10 ring-1 ring-primary-c/30" : "bg-surface-muted"}`}>
                  <p className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 text-center ${c === "oresto" ? "text-primary-c" : "text-text-secondary"}`}>{heads[c]}</p>
                  <Cell cell={r[c]} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="mt-10 hidden md:block" data-testid="comparison-table">
        <table className="w-full bg-white rounded-3xl overflow-hidden border border-c table-fixed">
          <thead>
            <tr className="bg-surface-muted">
              <th className="text-left px-5 py-4 text-sm font-semibold text-text-secondary uppercase tracking-wider w-[28%]">{t("compare.criteria")}</th>
              {COLS.map((c) => (
                <th key={c} className={`px-4 py-4 text-center ${c === "oresto" ? "bg-primary-c/10" : ""}`}>
                  <p className={`font-display text-lg ${c === "oresto" ? "text-primary-c" : ""}`}>{heads[c]}</p>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-c">
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="px-5 py-4 text-sm font-medium">{r.label}</td>
                {COLS.map((c) => (
                  <td key={c} className={`px-4 py-4 ${c === "oresto" ? "bg-primary-c/5" : ""}`}><Cell cell={r[c]} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-center text-xs text-text-secondary mt-4 max-w-2xl mx-auto leading-relaxed">{t("compare.footnote")}</p>
    </section>
  );
}
